import qs from 'qs'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import SearchPlace from './SearchPlace'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useCruiseSearchEnabled } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import { useTourV2SearchEnabled } from 'hooks/useTourV2SearchEnabled'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useTripMetadataPrefetch } from 'tripPlanner/hooks/api/trip'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { PlaceInputTrigger } from './Common/PlaceInputTrigger'

interface Props {
  tripId: string
  sectionId?: string
}

function SearchTourOrCruise({ tripId, sectionId }: Props) {
  const intl = useIntl()
  useTripMetadataPrefetch({ tripId })
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()
  const isCruiseSearchEnabled = useCruiseSearchEnabled()

  const { searchItem } = useContext(GlobalSearchStateContext)
  const source = useTripClientSource()
  const query = useMemo(
    () =>
      qs.stringify({
        destinationId: searchItem ?
          searchItem.value :
          ANYWHERE_SEARCH_ITEM.value,
        destinationName: searchItem ?
          searchItem.format.mainText :
          ANYWHERE_SEARCH_ITEM.format.mainText,
        source,
      }),
    [searchItem, source],
  )

  const onPrimaryActionClick = useCallback(() => {
    setImmersiveMode({ tripId, sectionId })
  }, [setImmersiveMode, sectionId, tripId])

  const isMobile = useScreenSizeOnly('mobile')

  const tourV2SearchEnabled = useTourV2SearchEnabled()

  const searchToursTitle = intl.formatMessage({
    id: 'GjdTQW3aoOm4E5AXnmUs',
    defaultMessage: 'Search tours',
    description: 'Modal title for tour search when cruise search is separate',
  })

  const searchToursAndCruisesTitle = intl.formatMessage({
    id: '5MEyzk1_s7Nnmt_jJ2Ya',
    defaultMessage: 'Search tours & cruises',
    description: 'Modal title for combined tour and cruise search in trip planner',
  })

  const defaultSubTitle = intl.formatMessage({
    id: 'Y4bfRE8erSe_sSJQthmJ',
    defaultMessage: 'Search by destination',
    description: 'Default placeholder text for destination search input in tour search',
  })

  const searchButtonLabel = intl.formatMessage({
    id: 'ltbVwTmjg3LdqkrSkW8M',
    defaultMessage: 'Search',
    description: 'Action button label to submit tour search in trip planner',
  })

  const viewOffersLabel = intl.formatMessage({
    id: 'H-t-eYWufbDXHtseBNvX',
    defaultMessage: 'View offers',
    description: 'Button text to view tour offers when search is not enabled',
  })

  return (
    <>
      <ModalHeader
        title={isCruiseSearchEnabled ? searchToursTitle : searchToursAndCruisesTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'ud_sP8l_ULsavYyNfsed',
                defaultMessage: 'Find the world’s best escapes, handpicked by experts',
                description: 'Promotional description text for tour search in trip planner',
              })}
            </BodyText>
            {tourV2SearchEnabled && (
              <>
                <PlaceInputTrigger
                  subTitle={searchItem?.format.mainText ?? defaultSubTitle}
                  onClick={() =>
                    openModal(
                      SearchPlace,
                      {
                        searchView: 'TOUR_AND_CRUISES' as const,
                        tripId,
                      },
                      {
                        height: 'full',
                      },
                    )}
                />
                <Group direction="horizontal-reverse">
                  <TextButton
                    kind="primary"
                    fit={isMobile ? 'full-width' : undefined}
                    to={'/search/tours?' + query}
                    onClick={onPrimaryActionClick}
                    size="large"
                  >
                    {searchButtonLabel}
                  </TextButton>
                </Group>
              </>
            )}
            {!tourV2SearchEnabled && (
              <TextButton
                kind="primary"
                fit="full-width"
                to="/tours"
                onClick={onPrimaryActionClick}
                size="large"
              >
                {viewOffersLabel}
              </TextButton>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SearchTourOrCruise
